// Iter76 — Admin Partner Products Approval Queue
// =================================================
// Lists every partner / franchisee product across the network. Master admin
// reviews and approves/rejects. Approve triggers an auto-sync to Square; if
// Square sync fails the product is held in `draft`/`is_published=false`
// and the failure is surfaced to the admin.
import React, { useEffect, useState } from "react";
import {
  CheckCircle2, XCircle, Loader2, AlertTriangle,
  Star, Filter, ExternalLink,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import SafeImage from "@/components/SafeImage";

const STATUS_FILTERS = [
  { key: "pending_review", label: "Pending Review" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "draft", label: "Drafts" },
  { key: "", label: "All" },
];

const CATEGORY_LABEL = {
  glass_gear: "Glass Gear",
  clothing: "Clothing / Lifted Gear",
  accessories: "Accessories",
  vape_hardware: "Vape Hardware",
  food_beverage: "Food & Beverage",
  topicals: "Topicals",
  tinctures: "Tinctures",
  edibles: "Edibles",
  cbd: "CBD",
  thca: "THCA / Restricted",
  other: "Other",
};

const STATUS_BADGE = {
  draft:           { label: "Draft",            cls: "bg-slate-500/20 text-slate-200 border-slate-400/30" },
  pending_review:  { label: "Pending Review",   cls: "bg-amber-500/20 text-amber-200 border-amber-400/30" },
  approved:        { label: "Approved · Live",  cls: "bg-emerald-500/20 text-emerald-200 border-emerald-400/30" },
  rejected:        { label: "Rejected",         cls: "bg-rose-500/30 text-rose-100 border-rose-400/40" },
};

export default function AdminPartnerProductsQueue() {
  const [status, setStatus] = useState("pending_review");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  async function reload() {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (status) params.status = status;
      const { data } = await api.get("/admin/partner-products", { params });
      setItems(data.items || []);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [status]);

  async function approve(id) {
    setBusyId(id); setError(null); setInfo(null);
    try {
      const { data } = await api.post(`/admin/partner-products/${id}/approve`);
      if (data.square_sync_status === "synced") {
        setInfo("Approved and synced to Square. Product is now live.");
      } else if (data.square_sync_status === "skipped") {
        setInfo("Approved. Square sync skipped (no Square credentials configured in this environment).");
      } else {
        setError(`Approved but Square sync failed: ${data.square_sync_error || "unknown"}. Product is HELD from public listing until the sync succeeds.`);
      }
      await reload();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  }

  async function reject(id) {
    const reason = window.prompt("Reason for rejection (will be sent to partner):", "");
    if (reason === null) return;
    setBusyId(id); setError(null); setInfo(null);
    try {
      await api.post(`/admin/partner-products/${id}/reject`, { reason });
      setInfo("Rejected.");
      await reload();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="admin-partner-products-queue">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-lifted-green">Master Admin</div>
        <h1 className="font-anton uppercase text-4xl text-white">Partner Products · Review Queue</h1>
        <p className="text-lifted-dim text-sm max-w-2xl mt-2">
          Approve partner/franchisee products. On approve, the product is
          auto-synced to Square. If Square fails, the product stays HELD
          (not publicly listed) until the sync succeeds.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <Filter size={14} className="text-lifted-dim" />
        {STATUS_FILTERS.map((s) => (
          <button
            key={s.key}
            onClick={() => setStatus(s.key)}
            data-testid={`admin-partner-products-filter-${s.key || "all"}`}
            className={`px-3 py-1.5 text-xs font-bebas tracking-wider uppercase border rounded-full ${
              status === s.key
                ? "bg-lifted-green/20 border-lifted-green/50 text-lifted-green"
                : "border-white/20 text-white/70 hover:bg-white/10"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="border border-rose-500/40 bg-rose-500/10 rounded-md p-3 text-sm text-rose-200 flex items-center gap-2"
             data-testid="admin-partner-products-error">
          <AlertTriangle size={14} /> {error}
        </div>
      )}
      {info && (
        <div className="border border-emerald-500/40 bg-emerald-500/10 rounded-md p-3 text-sm text-emerald-200 flex items-center gap-2"
             data-testid="admin-partner-products-info">
          <CheckCircle2 size={14} /> {info}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-lifted-dim"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center text-lifted-dim py-12 border border-dashed border-white/15 rounded-xl">
          No partner products matching this filter.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((p) => (
            <ProductRow key={p.id} p={p} busy={busyId === p.id} onApprove={approve} onReject={reject} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProductRow({ p, busy, onApprove, onReject }) {
  const badge = STATUS_BADGE[p.status] || { label: p.status, cls: "bg-white/10 text-white" };
  return (
    <article
      className="glass-panel p-4 grid grid-cols-[140px_1fr] gap-4"
      data-testid={`admin-partner-product-${p.id}`}
    >
      <div className="aspect-square w-[140px] bg-black/30 rounded-md overflow-hidden">
        <SafeImage src={(p.images || [])[0] || p.image_url} alt={p.title} className="w-full h-full object-cover" />
      </div>
      <div className="space-y-2 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-0.5 min-w-0">
            <div className="text-sm font-semibold text-white truncate" data-testid={`admin-partner-product-title-${p.id}`}>{p.title}</div>
            <div className="text-xs text-lifted-dim truncate">
              {p.partner_business_name || p.partner_slug || p.partner_id?.slice(0, 8)}
              {" · "}
              {CATEGORY_LABEL[p.category] || p.category}
            </div>
          </div>
          <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-bebas tracking-wider ${badge.cls}`}
                data-testid={`admin-partner-product-status-${p.id}`}>{badge.label}</span>
        </div>
        <div className="text-xs text-white/70 grid grid-cols-3 gap-1">
          <span>Price: ${((p.price_cents || 0) / 100).toFixed(2)}</span>
          <span>Cost: ${((p.cost_cents || 0) / 100).toFixed(2)}</span>
          <span>Stock: {p.inventory_count ?? 0}</span>
        </div>
        {p.description && <p className="text-xs text-lifted-dim line-clamp-2">{p.description}</p>}
        {p.age_restricted && (
          <div className="text-xs text-amber-200 inline-flex items-center gap-1 border border-amber-400/30 bg-amber-500/10 rounded px-2 py-0.5">
            <AlertTriangle size={10} /> 21+ · THC {p.thc_pct || "?"}
            {p.cbd_pct && ` · CBD ${p.cbd_pct}`}
          </div>
        )}
        {p.coa_url && (
          <a href={p.coa_url} target="_blank" rel="noreferrer"
             data-testid={`admin-partner-product-coa-${p.id}`}
             className="text-xs text-lifted-green underline inline-flex items-center gap-1">
            <ExternalLink size={10} /> COA
          </a>
        )}
        {p.label_image_url && (
          <div>
            <div className="text-[10px] text-lifted-dim uppercase tracking-wider mb-1">Label image</div>
            <SafeImage src={p.label_image_url} className="w-20 h-20 rounded object-cover border border-white/15" alt="label" />
          </div>
        )}
        {p.featured && (
          <div className="inline-flex items-center gap-1 text-xs text-lifted-green"
               data-testid={`admin-partner-product-featured-${p.id}`}>
            <Star size={12} /> Featured on partner storefront
          </div>
        )}
        {p.square_sync_status && p.status === "approved" && (
          <div className={`text-xs inline-flex items-center gap-1 ${p.square_sync_status === "synced" ? "text-emerald-300" : "text-rose-300"}`}
               data-testid={`admin-partner-product-square-${p.id}`}>
            Square: {p.square_sync_status}
            {p.square_sync_error && ` — ${p.square_sync_error}`}
          </div>
        )}

        {p.status === "pending_review" && (
          <div className="flex items-center gap-2 pt-2 border-t border-white/10">
            <button
              onClick={() => onApprove(p.id)}
              disabled={busy}
              data-testid={`admin-partner-product-approve-${p.id}`}
              className="btn-lifted inline-flex items-center gap-1 text-xs"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 size={12} />} Approve
            </button>
            <button
              onClick={() => onReject(p.id)}
              disabled={busy}
              data-testid={`admin-partner-product-reject-${p.id}`}
              className="btn-lifted-outline inline-flex items-center gap-1 text-xs text-rose-300 hover:text-rose-200"
            >
              <XCircle size={12} /> Reject
            </button>
          </div>
        )}
        {p.published_handle && (
          <a href={`/shop/products/${p.published_handle}`} target="_blank" rel="noreferrer"
             data-testid={`admin-partner-product-public-link-${p.id}`}
             className="text-xs text-lifted-green underline inline-flex items-center gap-1">
            <ExternalLink size={10} /> View public listing
          </a>
        )}
      </div>
    </article>
  );
}
